<template>
  <v-row>
    <Confirm
      :confirm.sync="confirmDelete"
      :callback="deleteInformationMessage"
      message="Are you sure you want to delete this information message?"
    ></Confirm>
    <v-speed-dial
      v-model="fab"
      :absolute="true"
      :top="true"
      :right="true"
      direction="bottom"
    >
      <template v-slot:activator>
        <v-btn v-model="fab" color="primary" dark fab>
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>mdi-information</v-icon>
        </v-btn>
      </template>
      <v-btn fab light small @click="showList = !showList">
        <v-icon>mdi-view-list</v-icon>
      </v-btn>
      <v-btn fab light small @click="newInformationMessage()">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn
        fab
        dark
        small
        color="red"
        @click="confirmDelete = true"
        v-if="informationMessage && informationMessage.id"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-speed-dial>
    <v-col class="px-6" cols="12" :md="showList ? 3 : 0" v-if="showList">
      <v-expansion-panels elevation="1" :value="0">
        <v-expansion-panel>
          <v-expansion-panel-header class="title">
            Information Messages
            <template v-slot:actions>
              <v-icon>mdi-alert-circle-outline</v-icon>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p>
              Information messages are messages that you regularly have to share
              with groups. Group members can access all your information
              messages using the
              <v-chip color="accent">/info</v-chip>command.
            </p>
            <p>
              The command will list your messages using the name field, so make
              it meaningful and short.
            </p>
            <p class="smaller">
              <b>Hint:</b> To change the order in which the information messages are displayed, choose to display all information messages in the pagination controls.<br>
              You will then be able to drag and reorder the messages.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-progress-linear
        indeterminate
        color="primary"
        v-if="infoLoading"
        height="7"
      ></v-progress-linear>

      <v-data-iterator
        :items="orderedInformation"
        item-key="id"
        :loading="infoLoading"
        :items-per-page.sync="limit"
        :server-items-length="totalInfoMessages"
        :page.sync="page"
        :footer-props="{
          'items-per-page-options': itemsPerPageArray,
          'items-per-page-text': '',
          'show-current-page': true,
        }"
        loading-text="Loading information messages..."
        no-results-text="You have not created any information messages yet."
      >
        <template v-slot:default="props">
          <v-list elevation="1">
            <v-list-item-group v-model="selected" color="primary">
              <draggable
                v-model="orderedInformation"
                @start="drag = true"
                @end="drag = false"
                :disabled="limit < totalInfoMessages && limit !== -1"
              >
                <v-list-item v-for="item in props.items" :key="item._id">
                  <v-list-item-icon>
                    <span
                      class="title"
                      v-text="nameIcon(item.name)"
                      v-if="nameIcon(item.name)"
                    ></span>
                    <v-icon v-else>mdi-message</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="name(item.name)"
                    ></v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-icon v-if="limit >= totalInfoMessages || limit === -1">
                    <v-icon class="drag-handle">mdi-drag</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </draggable>
            </v-list-item-group>
          </v-list>
        </template>
      </v-data-iterator>
    </v-col>
    <v-col class="px-6" cols="12" :md="showList ? 9 : 12">
      <InformationMessage
        :information-message="informationMessage"
        :groups="groups"
        v-if="informationMessage"
      ></InformationMessage>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { models, makeFindMixin, makeGetMixin } from "feathers-vuex";
import draggable from "vuedraggable";
import InformationMessage from "@/components/information/Message";
import Confirm from "@/components/widget/Confirm";

export default {
  data: () => ({
    itemsPerPageArray: [5, 10, 20, 30, 50, -1],
    page: 1,
    limit: 10,
    skip: 0,
    pagination: {
      rowsPerPage: 5,
    },
    fab: null,
    showList: true,
    selected: null,
    informationMessage: null,
    confirmDelete: false,
    configId: "infoMessageOrder",
  }),
  mounted() {},
  mixins: [
    makeFindMixin({ service: "information", watch: true }),
    makeFindMixin({ service: "groups" }),
    makeGetMixin({
      service: "configuration",
      id: "configId",
      name: "infoMessageOrderConfig",
    }),
  ],
  computed: {
    ...mapGetters("emoji", ["emojiRegex"]),
    infoLoading() {
      return this.isFindInformationPending;
    },
    totalInfoMessages() {
      return this.informationPaginationData.default &&
        this.informationPaginationData.default.mostRecent.total
        ? this.informationPaginationData.default.mostRecent.total
        : 0;
    },
    infoMessageOrder: {
      get() {
        return (
          this.infoMessageOrderConfig && this.infoMessageOrderConfig.config
        );
      },
      set(infoOrder) {
        this.infoMessageOrderConfig.config = infoOrder;
        this.infoMessageOrderConfig.save();
      },
    },
    orderedInformation: {
      get() {
        let ordered =
          this.information &&
          this.infoMessageOrder &&
          this.information.slice().sort((a, b) => {
            return (
              this.infoMessageOrder.indexOf(a.id) -
              this.infoMessageOrder.indexOf(b.id)
            );
          });

        return ordered || this.information;
      },
      set(info) {
        let order = [];
        for (let index = 0; index < info.length; index++) {
          let message = info[index];
          if (message.id) order.push(message.id);
        }
        this.infoMessageOrder = order;
      },
    },
    paginatedOrderIds() {
      return this.infoMessageOrder.slice(this.skip, this.limit);
    },
    informationParams() {
      return {
        query: {
          $limit: this.limit !== -1 ? this.limit : this.totalInfoMessages,
          $skip: this.skip,
        },
      };
    },
    groupsParams() {
      return { query: {
        deleted: false
      } };
    },
  },
  methods: {
    ...mapActions("app", ["showMessage"]),
    newInformationMessage() {
      const { Information } = models.bot;
      this.informationMessage = new Information();
    },
    async deleteInformationMessage() {
      if (this.informationMessage && this.informationMessage.id) {
        try {
          await this.informationMessage.remove();
          this.showMessage({
            text: "Information message deleted!",
            color: "green",
          });
          this.newInformationMessage();
        } catch (error) {
          this.error = error;
          this.showMessage({
            text: error.message || "Error occured!",
            color: "red",
          });
        }
      }
    },
    name(name) {
      return name.replace(this.emojiRegex, "");
    },
    nameIcon(name) {
      let emoji = name.match(this.emojiRegex);
      if (emoji) {
        return emoji[0];
      }
      return false;
    },
  },
  components: {
    InformationMessage,
    Confirm,
    draggable,
  },
  watch: {
    page(now) {
      this.skip = now * this.limit - this.limit;
      this.newInformationMessage();
      this.selected = null;
    },
    haveInformationLoadedOnce(loaded) {
      if (loaded) {
        this.newInformationMessage();
      }
    },
    selected(item) {
      console.log('selected', item)
      if (
        this.selected !== undefined &&
        this.selected !== null &&
        this.orderedInformation &&
        this.orderedInformation[item]
      ) {
        this.informationMessage = this.orderedInformation[item].clone();
      } else if (this.selected === undefined) {
        this.informationMessage = null;
      }
    },
    informationMessage(item) {
      if (
        this.selected !== undefined &&
        this.selected !== null &&
        this.orderedInformation &&
        this.orderedInformation[this.selected] &&
        this.orderedInformation[this.selected].id !== item.id
      ) {
        this.selected = null;
      } else if (
        (this.selected === undefined || this.selected === null) &&
        item &&
        item.id
      ) {
        this.selected = this.orderedInformation.length - 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.drag-handle {
  cursor: grab;
}

.smaller {
  font-size: 80%;
}
</style>
